/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { AttractionModal } from './components/AttractionModal';
import { FloatingContactBar } from './components/FloatingContactBar';
import { Attraction, Category, Language } from './types';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { trackPageView } from './utils/analytics';

// Dedicated Standalone Page Components
import { HomePage } from './pages/HomePage';

// Code-split subpages for maximum mobile performance and minimal initial bundle size
const loadRoadtripsPage = () => import('./pages/RoadtripsPage').then(m => ({ default: m.RoadtripsPage }));
const loadEventsPage = () => import('./pages/EventsPage').then(m => ({ default: m.EventsPage }));
const loadDrivingGuidePage = () => import('./pages/DrivingGuidePage').then(m => ({ default: m.DrivingGuidePage }));
const loadTravelTipsPage = () => import('./pages/TravelTipsPage').then(m => ({ default: m.TravelTipsPage }));

export const RoadtripsPage = React.lazy(loadRoadtripsPage);
export const EventsPage = React.lazy(loadEventsPage);
export const DrivingGuidePage = React.lazy(loadDrivingGuidePage);
export const TravelTipsPage = React.lazy(loadTravelTipsPage);

export async function preloadLazyComponent(lazyComp: any) {
  if (lazyComp && lazyComp._init && lazyComp._payload) {
    try {
      lazyComp._init(lazyComp._payload);
    } catch (p: any) {
      if (p && typeof p.then === 'function') {
        await p;
      }
    }
  }
}

export async function preloadRoute(url: string) {
  const clean = url.replace(/^\//, '').replace(/\/$/, '');
  const parts = clean.split('/').filter(Boolean);
  let slug = parts[0] || '';
  if (['es', 'en', 'pt'].includes(slug)) {
    slug = parts[1] || '';
  }

  if (['rutas', 'routes', 'roteiros'].includes(slug)) {
    await loadRoadtripsPage();
    await preloadLazyComponent(RoadtripsPage);
  } else if (['eventos', 'events'].includes(slug)) {
    await loadEventsPage();
    await preloadLazyComponent(EventsPage);
  } else if (['consejos-manejo', 'driving-guide', 'guia-direcao', 'consejos'].includes(slug)) {
    await loadDrivingGuidePage();
    await preloadLazyComponent(DrivingGuidePage);
  } else if (['consejos-viaje', 'travel-tips', 'dicas-viagem', 'viaje'].includes(slug)) {
    await loadTravelTipsPage();
    await preloadLazyComponent(TravelTipsPage);
  }
}

export async function preloadAllPages() {
  await Promise.all([
    loadRoadtripsPage().then(() => preloadLazyComponent(RoadtripsPage)),
    loadEventsPage().then(() => preloadLazyComponent(EventsPage)),
    loadDrivingGuidePage().then(() => preloadLazyComponent(DrivingGuidePage)),
    loadTravelTipsPage().then(() => preloadLazyComponent(TravelTipsPage))
  ]);
}

function MainLayout() {
  const { lang: urlLang } = useParams<{ lang: string }>();
  const { lang, setLang } = useLanguage();
  const location = useLocation();
  const [selectedAttraction, setSelectedAttraction] = useState<Attraction | null>(null);

  // Sync state with URL param
  useEffect(() => {
    if (urlLang && ['es', 'en', 'pt'].includes(urlLang)) {
      if (urlLang !== lang) {
        setLang(urlLang as Language);
      }
    }
  }, [urlLang, lang, setLang]);

  // Scroll to top on route change & track analytics
  useEffect(() => {
    trackPageView(location.pathname + location.search);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF8F5] text-stone-900 font-['Plus_Jakarta_Sans',sans-serif]">
      <Navbar onSelectCategory={() => {}} />

      <main className="flex-1">
        <Routes>
          {/* Main Home Page - Static and immediate for 0 Suspense overhead */}
          <Route 
            path="/" 
            element={<HomePage onAttractionClick={(attr) => setSelectedAttraction(attr)} />} 
          />

          {/* Rutas / Routes / Roteiros */}
          <Route path="/rutas" element={<React.Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center py-12"><div className="w-8 h-8 border-3 border-[#073B4C] border-t-[#D6A84B] rounded-full animate-spin" /></div>}><RoadtripsPage /></React.Suspense>} />
          <Route path="/routes" element={<React.Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center py-12"><div className="w-8 h-8 border-3 border-[#073B4C] border-t-[#D6A84B] rounded-full animate-spin" /></div>}><RoadtripsPage /></React.Suspense>} />
          <Route path="/roteiros" element={<React.Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center py-12"><div className="w-8 h-8 border-3 border-[#073B4C] border-t-[#D6A84B] rounded-full animate-spin" /></div>}><RoadtripsPage /></React.Suspense>} />

          {/* Eventos */}
          <Route path="/eventos" element={<React.Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center py-12"><div className="w-8 h-8 border-3 border-[#073B4C] border-t-[#D6A84B] rounded-full animate-spin" /></div>}><EventsPage /></React.Suspense>} />
          <Route path="/events" element={<React.Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center py-12"><div className="w-8 h-8 border-3 border-[#073B4C] border-t-[#D6A84B] rounded-full animate-spin" /></div>}><EventsPage /></React.Suspense>} />

          {/* Consejos de Manejo / Driving Guide */}
          <Route path="/consejos-manejo" element={<React.Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center py-12"><div className="w-8 h-8 border-3 border-[#073B4C] border-t-[#D6A84B] rounded-full animate-spin" /></div>}><DrivingGuidePage /></React.Suspense>} />
          <Route path="/driving-guide" element={<React.Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center py-12"><div className="w-8 h-8 border-3 border-[#073B4C] border-t-[#D6A84B] rounded-full animate-spin" /></div>}><DrivingGuidePage /></React.Suspense>} />
          <Route path="/guia-direcao" element={<React.Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center py-12"><div className="w-8 h-8 border-3 border-[#073B4C] border-t-[#D6A84B] rounded-full animate-spin" /></div>}><DrivingGuidePage /></React.Suspense>} />
          <Route path="/consejos" element={<React.Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center py-12"><div className="w-8 h-8 border-3 border-[#073B4C] border-t-[#D6A84B] rounded-full animate-spin" /></div>}><DrivingGuidePage /></React.Suspense>} />

          {/* Consejos de Viaje / Travel Tips */}
          <Route path="/consejos-viaje" element={<React.Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center py-12"><div className="w-8 h-8 border-3 border-[#073B4C] border-t-[#D6A84B] rounded-full animate-spin" /></div>}><TravelTipsPage /></React.Suspense>} />
          <Route path="/travel-tips" element={<React.Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center py-12"><div className="w-8 h-8 border-3 border-[#073B4C] border-t-[#D6A84B] rounded-full animate-spin" /></div>}><TravelTipsPage /></React.Suspense>} />
          <Route path="/dicas-viagem" element={<React.Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center py-12"><div className="w-8 h-8 border-3 border-[#073B4C] border-t-[#D6A84B] rounded-full animate-spin" /></div>}><TravelTipsPage /></React.Suspense>} />
          <Route path="/viaje" element={<React.Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center py-12"><div className="w-8 h-8 border-3 border-[#073B4C] border-t-[#D6A84B] rounded-full animate-spin" /></div>}><TravelTipsPage /></React.Suspense>} />
        </Routes>
      </main>

      <Footer />
      <AttractionModal
        attraction={selectedAttraction}
        onClose={() => setSelectedAttraction(null)}
      />
      <FloatingContactBar />
    </div>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <Routes>
        <Route path="/:lang/*" element={<MainLayout />} />
        <Route path="/*" element={<MainLayout />} />
      </Routes>
    </LanguageProvider>
  );
}
